import type { ApiResponse } from '@safetyhub/shared';
import api from './api';

/**
 * **전사 공지 저작** API 클라이언트 — 「전사 공지」 페이지(`/announcements`)가 쓴다.
 *
 * base `/admin/announcements`. 계약 정본은 서버 `announcements/dto/announcement.dto.ts`와
 * `AdminAnnouncementsController` JSDoc이고, 줄기는 `popup-and-announcement-surface` **P3-u**다.
 *
 * ## ⚠️ `organizationId`를 보내지 않는다 — 보낼 칸이 없다
 *
 * 서버가 `null`을 **박는다**(전사). body로 받으면 이 경로로 **특정 조직의 공지**를 만들 수 있고
 * 그것은 조직 저작 경로(권한·감사 귀속이 다르다)를 우회한다 — 그 금지는 서버 DTO의 계약이고
 * 여기 타입에 그 칸을 두지 않는 것이 그 계약을 **클라이언트에서도 표현 불가로** 만든다.
 * ⚠️ `forbidNonWhitelisted`라 보내면 400이지만, 타입에 없으면 **쓸 생각조차 안 하게** 된다.
 *
 * ## ⚠️ 대상 조직 후보는 `/admin/companies`다 — `/admin/subscriptions`로 바꾸지 말 것
 *
 * 「구독 목록이 더 적절해 보인다」는 자연스러운 오독이고(그쪽이 「조직 목록」이라 불리며 선례
 * `pages/MessageSend.tsx`가 `getSubscriptions({ limit: 1000 })`을 쓴다) **바꾸면 기능이 줄어든다**:
 * 그 엔드포인트는 **`category=WORKSPACE`를 제외한다**(`admin.service.ts`의 `getOrganizations`).
 * 개인 작업공간도 구성원이 있는 **실테넌트**이므로 후보가 **겨눌 수 있는 집합보다 좁아지고**,
 * 특히 `EXCLUDE`에서 **개인 작업공간을 뺄 길이 없어진다**. 그 결함은 조용하다 — 목록에 없으니
 * 운영자는 그런 조직이 없다고 믿는다.
 *
 * ⇒ `/admin/companies`는 **상위집합**이고 `q`로 서버 검색을 하며 `@RequirePermission`이 **없다**
 * (`SuperAdminGuard`만) ⇒ 권한 축이 이 화면(`system-settings`)과 어긋나지 않는다. 상위집합이라
 * 반대 방향(테넌트가 아닌 회사를 겨눔)이 열리는데, 그것은 **막지 않고** `hasSubscription`을 중립
 * 배지로 **신호만** 준다(상세는 `components/OrgTargetCombobox.tsx` docblock).
 * ⚠️ 즉 두 선택지는 **넓은 쪽 / 좁은 쪽**이고 우리는 **넓은 쪽**을 골랐다. 좁히려면 「개인
 * 작업공간을 겨눌 수 없게 한다」는 **정책 결정**이 먼저다 — 후보 목록을 바꾸는 일이 아니다.
 *
 * ## ⚠️ 조직 범위는 `orgScope` + 목록을 **언제나 함께** 보낸다
 *
 * 수정에서 모드만 바꾸면 서버가 `ANNOUNCEMENT_ORG_SCOPE_CHANGE_NEEDS_TARGETS`로 거부한다 —
 * 옛 목록이 새 모드의 뜻으로 재해석되어 **노출 대상이 조용히 뒤집히기** 때문이다
 * (`INCLUDE [orgA]` → `EXCLUDE`면 「orgA만」이 「orgA만 빼고 전부」가 된다). 화면이 두 값을
 * 한 상태로 들고 **항상 둘을 함께** 보내므로 그 400에 닿지 않는다.
 *
 * ## 응답 봉투
 *
 * 서버 전역 `ResponseInterceptor`가 모든 응답을 `{ success, data, error }`로 감싼다 — 컨트롤러가
 * 날것을 돌려주더라도(`return this.authoring.listCompanyWide()`) 그 형상으로 온다. 그래서
 * `res.data.data`가 정본 경로다.
 *
 * ⚠️ **그 봉투 타입은 shared `ApiResponse`를 import한다 — 여기 다시 선언하지 말 것.**
 * 2026-10-21까지 이 파일은 그것을 **지역 선언**하고 있었고 선례로 `company-rbac.ts`를 가리켰는데,
 * 그 선례 **자신이 또 다른 사본**이다 — 즉 **사본이 사본을 번식시켰다.** 당시 전수는
 * **13파일**(admin 9 · web 4)이고 shared 정본을 import하는 자리는 **0건**이었다.
 *
 * 형상 차이가 실질적이라 그 번식이 조용하지 않다: 정본은 **`data: T | null`**이고 사본들은
 * `data: T`다 ⇒ 사본을 쓰면 **null 체크를 tsc가 요구하지 않는다**(봉투가 `data: null`로 오는 길이
 * 실재하는데 호출부는 그것을 모른다). 그래서 이 파일은 그 null을 {@link unwrapEnvelope}에서
 * **한 번 가른다** — 조용히 `as`로 지우지 않는다.
 *
 * ⚠️ 나머지 12자리의 이관은 **레포 차원 결정**이라 이 작업 범위가 아니다(호출부 파급이 넓다).
 * 여기서 닫은 것은 「새 파일이 13번째를 더하지 않는다」 하나다.
 */

/**
 * 봉투에서 본문을 꺼낸다 — 정본 타입이 `data: T | null`이라 그 null을 **여기서 한 번** 가른다.
 *
 * `null`은 서버가 2xx로 본문 없이 응답했다는 뜻이고 그것은 계약 위반이다(에러는 axios가 throw
 * 한다) ⇒ 폴백을 두지 않는다. 특히 목록에 `?? []`를 쓰면 **깨진 봉투가 「공지가 없다」로 보여**
 * 운영자가 자기 공지가 사라진 것으로 읽는다.
 */
function unwrapEnvelope<T>(envelope: ApiResponse<T>): T {
  if (envelope.data === null) {
    throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
  }
  return envelope.data;
}

const BASE = '/admin/announcements';

export const ADMIN_ANNOUNCEMENT_QUERY_KEYS = {
  list: ['admin', 'announcements'] as const,
};

/**
 * 노출 surface — 서버 DTO의 `SURFACE_PLATFORMS`와 **같은 집합**이다.
 *
 * ⚠️ **여기 손열거하는 것이 불가피하다** — 서버 쪽은 Prisma enum(`SurfacePlatform`)에서 파생하는데
 * admin은 `@prisma/client`를 import할 수 없고 shared에도 그 enum이 **없다**(shared의
 * `audience-targeting.ts` docblock이 「매핑을 여기 선언하면 두 enum의 유산 값이 shared로 새어
 * 들어온다」로 그 부재를 설명한다).
 *
 * ⇒ 그래서 이 목록이 낡는 방향은 **한 쪽뿐**이다: 서버가 네 번째 surface를 받게 되면 이 화면이
 * 그것을 **제시하지 않는다**(저장은 여전히 옳고, 그 surface를 고를 수 없을 뿐이다). 반대 방향
 * (여기 있는데 서버가 거부)은 서버 `@IsIn`이 400으로 **시끄럽게** 말한다. 안전한 쪽으로 낡는다.
 *
 * ⚠️ **그 「제시하지 않는다」에는 red가 없다** — surface를 더하는 사람이 읽을 자리는
 * `apps/server/src/audience/surface-namespace.ts`의 `namespaceOfPlatform`(그 `never` 분기가 분류를
 * tsc로 강제한다)이고, 그 자리에서 **이 화면도 함께** 고칠 것.
 */
export const ANNOUNCEMENT_SURFACES = ['WEB', 'APP', 'ADMIN'] as const;
export type AnnouncementSurface = (typeof ANNOUNCEMENT_SURFACES)[number];

/**
 * 조직 범위 모드 — 서버 DTO의 `ORG_SCOPES`와 같은 집합.
 *
 * 모드가 **부모 칸**인 것이 설계다(`OrgScope` enum JSDoc) — 포함·제외가 한 목록에 섞이는 상태를
 * **표현 불가**로 만든다. 화면도 그 형상을 그대로 쓴다(라디오 + 목록).
 */
export const ANNOUNCEMENT_ORG_SCOPES = ['ALL', 'INCLUDE', 'EXCLUDE'] as const;
export type AnnouncementOrgScope = (typeof ANNOUNCEMENT_ORG_SCOPES)[number];

/** 조직 범위 모드의 화면 문구 — 운영자는 `INCLUDE`가 아니라 이 말로 읽는다. */
export const ANNOUNCEMENT_ORG_SCOPE_LABELS: Record<AnnouncementOrgScope, string> = {
  ALL: '전 조직',
  INCLUDE: '고른 조직만',
  EXCLUDE: '고른 조직 빼고 전부',
};

/** surface의 화면 문구 — `ADMIN`은 운영자에게 「관리자 페이지」다. */
export const ANNOUNCEMENT_SURFACE_LABELS: Record<AnnouncementSurface, string> = {
  WEB: '웹',
  APP: '앱',
  ADMIN: '관리자 페이지',
};

/**
 * 저작 목록의 한 행 — `GET /admin/announcements`가 돌려주는 `Announcement` + 대상 조직.
 *
 * ⚠️ **초안·숨김·만료를 모두 포함한다**(저작 목록이다) — 뷰어가 보는 목록과 다른 쿼리이고
 * 서버가 두 메서드로 **갈라 두었다**(`listBoard` ∥ 이것). 「노출 후보」로 읽지 말 것.
 */
export interface AdminAnnouncement {
  id: string;
  /** 전사 공지는 **언제나 null**이다 — 서버가 박는다 */
  organizationId: string | null;
  title: string;
  body: string;
  platforms: AnnouncementSurface[];
  /** `<네임스페이스>:<역할>` 집합. **빈 배열 = 모두**(「아무에게도」가 아니다) */
  audiences: string[];
  orgScope: AnnouncementOrgScope;
  pinned: boolean;
  startsAt: string | null;
  endsAt: string | null;
  hidden: boolean;
  /** null = 미게시(초안) — 목록에도 띠에도 들지 않는다 */
  publishedAt: string | null;
  /** 팝업 재노출 판정 기준 시각 — `title`·`body`가 **실제로 바뀔 때만** 서버가 올린다 */
  contentRevisedAt: string;
  /** 선택 병행 팝업의 id. 공지가 팝업을 호출하고 FK는 이쪽에만 있다 */
  popupId: string | null;
  /**
   * 병행 팝업의 두 옵션 — 저작 목록이 함께 싣는다(서버 `AUTHORING_POPUP_INCLUDE`). 연결이 없으면 null.
   * `string`인 이유: 와이어 값이 열려 있다(서버 enum이 늘면 옛 번들이 그것을 받는다).
   */
  popup?: { frequency: string; optOut: string } | null;
  /** 전사 공지는 null(귀속은 `audit_logs`가 가진다) */
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  /** 대상 조직 — `orgScope`가 뜻을 정한다. `ALL`이면 **0개** */
  orgTargets: Array<{ organizationId: string }>;
}

/**
 * 생성·수정이 **공유하는** 입력 — 화면이 한 상태로 들고 두 요청에 같은 형상으로 싣는다.
 *
 * ⚠️ 수정은 서버가 전부 optional로 받지만(`UpdateCompanyWideAnnouncementDto`) 화면은 **전량**을
 * 보낸다. 부분 전송은 「무엇이 빠졌나」를 호출부가 계산하게 만들고, 특히 `orgScope`·타깃은
 * **함께 보내야** 하므로(위 docblock) 둘만 예외로 두면 그 규칙이 조용히 어긋난다.
 */
export interface AnnouncementAuthoringInput {
  title: string;
  body: string;
  /** **최소 하나** — 빈 배열은 서버가 `ANNOUNCEMENT_PLATFORMS_REQUIRED`로 거부한다 */
  platforms: AnnouncementSurface[];
  /** 빈 배열 = 모두에게 */
  audiences: string[];
  orgScope: AnnouncementOrgScope;
  /** `ALL`이면 **빈 배열이어야 한다**(서버가 짝을 본다) */
  targetOrganizationIds: string[];
  pinned: boolean;
  /** ISO 8601 UTC. null = 하한·상한 없음 */
  startsAt: string | null;
  endsAt: string | null;
  /** true면 즉시 게시 · false면 초안. 수정에서 false면 초안으로 되돌린다 */
  publish: boolean;
  /**
   * 작성자가 내려 둔 상태 — 기간·게시와 독립. ⛔ **수정에서만 보낸다** — 생성 DTO
   * (`CreateCompanyWideAnnouncementDto` = `AnnouncementContentDto` 상속)에 그 칸이 **없고**
   * `forbidNonWhitelisted`라 생성에 실으면 400 `property hidden should not exist`다(web 짝이
   * 2026-10-27 dev 실측으로 같은 결함을 고쳤다 · 2026-10-29 이 화면에서 같은 형태를 발견).
   */
  hidden?: boolean;
  /**
   * 「팝업으로도 알리기」(결정 2026-10-26 개정 Q4) — 객체면 켠다. **수정에서 `null`은 끈다**(연결
   * 팝업이 지워진다) · 생략은 유지다. 생성에서는 끈 상태를 **생략**으로 보낸다.
   */
  popup?: AnnouncementPopupOptionsInput | null;
  /**
   * 「좁히지 않은 체계의 **전원**이 대상이 된다」를 운영자가 확인했나 — **저장되지 않는 칸**이다
   * (행위에 대한 확인이고 감사 로그에 남는다).
   *
   * ⚠️ **필요 없을 때는 `undefined`로 둔다 — `false`를 보내지 말 것.** 기본값의 소유자는 서버
   * 하나이고(그쪽이 fail-closed로 거부 쪽을 쓴다) 여기서 번역하면 기본값이 두 곳이 된다. 그
   * 어긋남은 응답 형상이 같아 **조용하다**(선례: `ReleaseNotes.handleSaveAudiences`의 같은 주석).
   */
  acknowledgeUnconstrainedNamespaces?: boolean;
}

/**
 * 「팝업으로도 알리기」의 두 옵션 — 서버 `AnnouncementPopupOptionsDto`와 같은 칸이다. 뜻의 정본은
 * 결정 `docs/decisions/2026-10-26-popup-close-is-a-session-and-opt-out-is-an-option.md`의 ⓐ 표다.
 */
export interface AnnouncementPopupOptionsInput {
  frequency: 'ONCE' | 'EVERY_VISIT';
  optOut: 'NONE' | 'HIDE_TODAY' | 'NEVER_AGAIN';
}

/** 재노출 문구 — exhaustive `Record`라 값이 늘면 tsc가 여기서 깨진다(web 짝과 같은 문구) */
export const ANNOUNCEMENT_POPUP_FREQUENCY_LABELS: Record<AnnouncementPopupOptionsInput['frequency'], string> = {
  ONCE: '한 번 — 읽으면 다시 뜨지 않음',
  EVERY_VISIT: '매번 — 접속할 때마다 다시 뜸',
};

/** opt-out 문구 — 보는 사람에게 보일 버튼 이름을 그대로 쓴다 */
export const ANNOUNCEMENT_POPUP_OPT_OUT_LABELS: Record<AnnouncementPopupOptionsInput['optOut'], string> = {
  NONE: '없음',
  HIDE_TODAY: '「오늘 하루 보지 않기」',
  NEVER_AGAIN: '「다시 보지 않기」',
};

/** 켤 때 기본값 — 「가벼운 공지」(`ONCE` + 「오늘 하루」 · 개정 Q4). 서버 기본값과 같다 */
export const DEFAULT_ANNOUNCEMENT_POPUP_OPTIONS: AnnouncementPopupOptionsInput = {
  frequency: 'ONCE',
  optOut: 'HIDE_TODAY',
};

export const adminAnnouncementsApi = {
  async list(): Promise<AdminAnnouncement[]> {
    const res = await api.get<ApiResponse<AdminAnnouncement[]>>(BASE);
    return unwrapEnvelope(res.data);
  },

  async create(body: AnnouncementAuthoringInput): Promise<AdminAnnouncement> {
    const res = await api.post<ApiResponse<AdminAnnouncement>>(BASE, body);
    return unwrapEnvelope(res.data);
  },

  async update(
    announcementId: string,
    body: AnnouncementAuthoringInput,
  ): Promise<AdminAnnouncement> {
    const res = await api.patch<ApiResponse<AdminAnnouncement>>(
      `${BASE}/${encodeURIComponent(announcementId)}`,
      body,
    );
    return unwrapEnvelope(res.data);
  },

  /** 삭제 — 봤음·대상 조직 행은 FK CASCADE가 함께 지운다(서버 컨트롤러 JSDoc) */
  async remove(announcementId: string): Promise<void> {
    await api.delete(`${BASE}/${encodeURIComponent(announcementId)}`);
  },
};

/**
 * 「한쪽 체계만 좁혔다」 승인을 서버가 요구하는 에러 코드 — 화면이 **그때만** 승인란을 띄운다.
 *
 * ⭐ **이 JSDoc이 「왜 선제 판정을 하지 않나」의 정본 한 벌이다.** 공지 저작 화면
 * (`pages/Announcements.tsx`)과 그 모달은 **여기를 가리키기만** 한다 — 본문을 베끼면 두 벌이
 * 되어 한쪽이 낡는다(팀장 요구 2026-10-20).
 *
 * ## ⚠️ 이 판정을 클라이언트에서 «미리» 하지 않는다 — 선례의 명시 결정이다
 *
 * `platforms` → 역할 체계 매핑은 **서버에만** 있다(`apps/server/src/audience/surface-namespace.ts`의
 * `impliedNamespaces` · admin에서의 import **0건**). Prisma enum을 키로 하고 shared는 그 enum을
 * 모르므로(shared `audience-targeting.ts`의 `unreachableAudienceNamespaces` docblock이 그 부재를
 * 설명한다) admin이 가져올 길이 없다. 베끼면 **같은 규약의 두 번째 구현**이 되고, 그 둘이 갈리는
 * 날 화면은 「고를 수 있다」는데 서버가 거부하거나 **반대로 화면이 막는데 서버는 허용한다**(그쪽이
 * 더 나쁘다 — 운영자가 할 수 있는 일을 못 한다고 믿는다).
 *
 * 그 결정의 **선례 자리**는 `components/ReleaseNoteAudienceModal.tsx` docblock의 「⚠️ 「닿지 않는
 * 체계를 제약했다」는 서버가 거부한다」 절이다 — **그 절을 읽을 것**(여기 옮겨 적지 않는다).
 *
 * ⚠️ **그 모달의 클라이언트측 근사를 베끼지 않았다.** 그쪽은 「닿는 체계」를 `useAudienceOptions`의
 * **그룹 전체**로 보는데, 공지 저작 화면은 `platforms`를 직접 고르므로 그 근사가 **거짓 승인 요구**를
 * 만든다 — `WEB`만 고른 공지에 「슈퍼관리자 전원에게 보여도 됩니까」를 묻게 되고, 슈퍼관리자는
 * 그 공지를 **아예 볼 수 없다**(그 조합은 서버에서 승인이 필요하지도 않다).
 *
 * ## ⚠️ 그 선택의 «대가» — 첫 전송이 400을 받는다 (의도된 거래다)
 *
 * 선제 판정을 하지 않으므로 그 조합을 쓰는 운영자는 **항상 왕복을 한 번 더 돈다**(선례는 승인란을
 * 미리 띄운다). 그것을 **빠뜨린 선제 검증으로 읽지 말 것** — 근사를 베껴 넣으면 위의 거짓 승인
 * 요구가 생기므로, 정확성을 택하고 왕복을 지불한 것이다. 흐름은 「저장 → 400 → 서버 메시지 +
 * 승인란 → 재전송」이고 **막다른 길이 아니다**(회복 경로가 화면에 있고, 모달이 닫히지 않아 입력을
 * 잃지 않는다).
 *
 * ## ⇒ 되짚을 조건 — 그 조건은 이제 «두 자리»를 깨운다
 *
 * **조건**: `impliedNamespaces`(또는 그에 상당하는 surface→체계 사상)가 **shared로 올라가** admin이
 * 읽을 수 있게 되는 때.
 * **재는 법**: `git grep -n "impliedNamespaces" -- packages/shared/src`가 0건이 아니게 되는 때.
 * **그때 함께 바꿀 두 자리**: ⓐ `components/ReleaseNoteAudienceModal.tsx`(선례 — 그 docblock이
 * 이미 「매핑이 올라오면 선제 차단이 낫다」고 적는다) ⓑ **이 화면**(`pages/Announcements.tsx` —
 * 선제 차단 + 위 왕복 제거). ⚠️ **한 자리만 고치면 두 화면의 판정이 갈린다** — 그래서 선례
 * docblock에도 「공지 저작 화면이 같은 판단을 공유한다」를 적어 두었다(2026-10-20).
 */
export const ANNOUNCEMENT_UNCONSTRAINED_CODE = 'ANNOUNCEMENT_AUDIENCE_NAMESPACE_UNCONSTRAINED';
